/**
 * RecipientAutocomplete — the "Who are you communicating with?" surface for Compose.
 *
 * Wraps a normal address input (To / Cc / Bcc). As the barrister types the CURRENT recipient token
 * (the text after the last comma/semicolon), it resolves people via the canonical Identity Service
 * and offers an autocomplete. Selecting a candidate inserts their email; typing a full address by
 * hand still works exactly as before (the manual comma/semicolon parse is untouched) — the
 * autocomplete is additive, never a gate.
 *
 * v1 is deliberately simple: an autocomplete over the field. NO recent/frequent/context panels
 * (those are deferred until telemetry shows they are worth building — Build → Use → Observe → Expand).
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  recordIdentitySearchTelemetry, classifyQueryType,
  type IdentityMatch,
} from '@/lib/identityApi';
import { useIdentitySearch } from '@/lib/useIdentitySearch';

interface Props {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

/** The token currently being edited = text after the last comma/semicolon. */
function currentToken(value: string): string {
  const parts = value.split(/[,;]/);
  return (parts[parts.length - 1] ?? '').trim();
}

/** Replace the trailing (in-progress) token with `email`, leaving earlier recipients intact. */
function replaceCurrentToken(value: string, email: string): string {
  const idx = Math.max(value.lastIndexOf(','), value.lastIndexOf(';'));
  const head = idx >= 0 ? value.slice(0, idx + 1) + ' ' : '';
  return `${head}${email}, `;
}

function matchReasonLabel(m: IdentityMatch): string | null {
  switch (m.matchedOn.kind) {
    case 'matter': return `via ${m.matchedOn.matterNumber}`;
    case 'organisation': return m.matchedOn.value;
    case 'phone': return m.matchedOn.value;
    default: return null;
  }
}

export function RecipientAutocomplete({ value, onChange, placeholder, className, autoFocus }: Props) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Live viewport rect of the input so the portalled dropdown can anchor to it.
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

  const token = currentToken(value);
  const { results, emptyResolved, searchStartRef } = useIdentitySearch(token);

  // Open the dropdown whenever there is something to show for the current token.
  useEffect(() => {
    if (results.length > 0 || emptyResolved) { setOpen(true); setActiveIdx(0); }
  }, [results, emptyResolved]);

  // Close the dropdown on an outside click.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Keep the portalled dropdown anchored to the input while it is open. The composer is a docked,
  // `overflow:hidden` panel, so an in-flow absolute dropdown would be CLIPPED — we portal it to
  // <body> at the input's viewport rect instead, and re-measure on scroll/resize.
  useEffect(() => {
    if (!open) return;
    const measure = () => { if (inputRef.current) setAnchorRect(inputRef.current.getBoundingClientRect()); };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true); // capture: catch scrolls in any ancestor
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open]);

  function pick(match: IdentityMatch, position: number) {
    const email = match.identity.emails[0];
    if (!email) return; // a court/organisation with no email can't be a recipient (yet)
    onChange(replaceCurrentToken(value, email));
    recordIdentitySearchTelemetry({
      query: token,
      queryType: classifyQueryType(token),
      resultCount: results.length,
      selectedKind: match.identity.ref.kind,
      selectedPosition: position,
      timeToSelectionMs: searchStartRef.current ? Date.now() - searchStartRef.current : null,
    });
    setOpen(false);
  }

  /** User dismissed the dropdown with a query that returned nothing — record it (ranking evidence). */
  function recordNoResultIfAny() {
    if (emptyResolved) {
      recordIdentitySearchTelemetry({
        query: token,
        queryType: classifyQueryType(token),
        resultCount: 0,
        noResult: true,
      });
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(results[activeIdx]!, activeIdx); }
    else if (e.key === 'Escape') { setOpen(false); }
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => { if (results.length) setOpen(true); }}
        onBlur={recordNoResultIfAny}
        placeholder={placeholder}
        className={className}
        autoFocus={autoFocus}
        autoComplete="off"
        style={{ fontFamily: "'IBM Plex Sans', sans-serif", width: '100%' }}
      />
      {open && anchorRect && (results.length > 0 || emptyResolved) && createPortal(
        <div
          style={{
            position: 'fixed',
            top: anchorRect.bottom + 2, left: anchorRect.left, width: anchorRect.width,
            zIndex: 1000, maxHeight: 260, overflowY: 'auto',
            background: 'var(--surface-card)', border: '1px solid var(--border-default)',
            borderRadius: 8, boxShadow: '0 6px 24px rgba(0,0,0,0.16)',
          }}
        >
          {results.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-subtle)' }}>
              No match — type a full email address to use it.
            </div>
          ) : results.map((m, i) => {
            const email = m.identity.emails[0];
            const reason = matchReasonLabel(m);
            return (
              <div
                key={`${m.identity.ref.kind}:${m.identity.ref.id}`}
                // onMouseDown (not onClick) so it fires before the input's onBlur closes the list.
                onMouseDown={(e) => { e.preventDefault(); pick(m, i); }}
                onMouseEnter={() => setActiveIdx(i)}
                style={{
                  padding: '8px 12px', cursor: email ? 'pointer' : 'not-allowed',
                  background: i === activeIdx ? 'var(--surface-hover)' : 'transparent',
                  opacity: email ? 1 : 0.5,
                  borderBottom: '1px solid var(--border-subtle)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                    {m.identity.displayName}
                  </span>
                  {m.identity.relationship && (
                    <span style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{m.identity.relationship}</span>
                  )}
                  {reason && (
                    <span style={{ fontSize: 11, color: '#2563eb' }}>{reason}</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-subtle)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {email ?? 'No email on file'}
                  {m.identity.organisation && email ? ` · ${m.identity.organisation}` : ''}
                </div>
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
